import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealPrompt } from '../../hooks/useDealPrompt';
import { type DealPromptContextType, dealPromptContextTypes } from '../../utils/types';
import { getSelectorLabel, selectorIcon } from './const';

type DealPromptContextTypeSelectorProps = {
  onChange: (type: DealPromptContextType) => void;
};

export const DealPromptContextTypeSelector = ({ onChange }: DealPromptContextTypeSelectorProps) => {
  const { t } = useTranslation();
  const {
    state: { contextType, loading },
  } = useDealPrompt();

  const selectors = Object.values(dealPromptContextTypes);

  const disabledSelectors: DealPromptContextType[] = [dealPromptContextTypes.ALL];

  const handleOnClick = useCallback(
    (type: DealPromptContextType) => {
      if (type !== contextType) {
        onChange(type);
      }
    },
    [contextType, onChange]
  );

  return (
    <Stack sx={{ position: 'relative', width: '100%' }} direction="row" spacing={1}>
      {selectors.map(type => {
        const Icon = selectorIcon[type];
        const disabled = loading || disabledSelectors.includes(type);
        const isSelected = contextType === type;

        return (
          <Button
            key={type}
            aria-selected={isSelected}
            variant={isSelected ? 'contained' : 'outlined'}
            disabled={disabled}
            sx={theme => ({
              borderRadius: 2,
              borderColor: 'divider',
              textTransform: 'none',
              minWidth: 'fit-content',
              color: 'text.secondary',
              ...(isSelected &&
                disabled && {
                  opacity: '0.4 !important',
                  backgroundColor: `${theme.palette.primary.main} !important`,
                  color: `${theme.palette.common.white} !important`,
                }),
            })}
            data-pendo-feature={`deal-prompt-main-tab-${type}`}
            onClick={handleOnClick.bind(this, type)}
          >
            <Icon
              sx={{
                fontSize: 18,
                // workaround for custom domains icon
                mr: type === dealPromptContextTypes.DOMAINS ? 0.75 : 1,
              }}
            />

            {getSelectorLabel(t)[type]}
          </Button>
        );
      })}
    </Stack>
  );
};
